import classNames from 'classnames';
import { SectionContainer } from 'components/section-container.js';
import { DomRefContext, DomRefType } from 'page-context/domRef/domRefProvider.js';
import { StickyPosition, StickyPositionProps } from 'page-context/scrollStickyStateProvider/scrollStickyMain.js';
import { AppBarStickyState } from 'page-context/scrollStickyStateProvider/stickybar-types.js';
import { memo, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { INavigation } from 'redux-state/layout/types.layout.base.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { IMenuItem } from 'types/menu.js';
import { zIndexNavigation } from 'utilities/css-variables.js';
import { getShouldDisplaySticky } from './is-current-url.js';
import { NavMenuActiveCB, NavMenuItemList } from './nav-menu-item-list.js';
import { trackNavigateEvent } from 'utilities/posthog-tracking';

interface ISecondaryNavigationProps {
  navigation: INavigation | undefined;
  searchBarDomRef: DomRefType['searchBarDomRef'] | undefined;
  secondaryNavBarDomRef: DomRefType['secondaryNavBarDomRef'] | undefined;
  dynamicBarPos: StickyPositionProps['dynamicBarPos'];
  stickyItemsState: AppBarStickyState | undefined;
}

enum StickyState {
  HIDDEN = 'hidden',
  SHOWING = 'showing',
}

const urlCheckCB: NavMenuActiveCB = ({ linkUrl, currentUrl }) => linkUrl === currentUrl;

export const SecondaryNavigation = ({
  stickyItemsState,
  navigation,
  searchBarDomRef,
  secondaryNavBarDomRef,
  dynamicBarPos,
}: ISecondaryNavigationProps) => {
  const containerClassName = `${dynamicBarPos ? StickyState.HIDDEN : StickyState.SHOWING}`;
  // In one-column view when the search container becomes 'sticky' we want the contents to be repositioned outside the container
  // immediately otherwise the search will appear and animate out

  const menuItems = useMemo(() => {
    const links = navigation?.links ?? [];
    const linkItems = links.map((item) => {
      const menuItem: IMenuItem = {
        name: item.label,
        link: item.url,
        target: item.target,
      };
      return menuItem;
    });
    return linkItems;
  }, [navigation?.links]);

  const onMenuItemClick = ({ item }: { item: IMenuItem }) => {
    // We want to track clicks on the secondary nav, but the item names are not in a good format for analytics, so we convert them to a more readable format
    // For example, '/weather/radar' could become 'weather-radar'
    trackNavigateEvent('sub_header', item.name + "_tab");
  }


  if (navigation?.links?.length) {
    const navClasses = classNames('Nav--secondary-wrapper'); //, className, { 'u-pB-sm': addMargin });
    const shouldDisplaySticky = getShouldDisplaySticky(stickyItemsState);

    return (
      <div
        data-module="secondary-navigation-entry"
        className="StickyNav"
        style={{
          position: shouldDisplaySticky ? 'sticky' : 'static',
          top: (searchBarDomRef?.height ?? 0) + dynamicBarPos,
          zIndex: shouldDisplaySticky ? zIndexNavigation : 'auto',
        }}
      >
        <SectionContainer
          className={`${containerClassName}`}
          fill="solid"
          ref={(el: HTMLDivElement) => secondaryNavBarDomRef?.setDomRef(el)}
          dataModule="secondary-navigation"
        >
          <div className={navClasses}>
            <nav className="Nav Nav--secondary">
              <div className="Container">
                <div className="Nav-scroller">
                  <div className="Nav-menu">
                    <ul className="Nav-menu-list">
                      <NavMenuItemList 
                        menu={menuItems} 
                        activeCB={urlCheckCB}
                        onClick={onMenuItemClick} 
                        className="Nav-menu-list-item" 
                      />
                    </ul>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </SectionContainer>
      </div>
    );
  }
  return null;
};

/** We memoise here as we dont want StickyBarContext to constantly cause rerenders (when scrolling) on children */
const SecondaryNavigationMemoized = memo(function SecondaryNav(props: ISecondaryNavigationProps) {
  return <SecondaryNavigation {...props} />;
});

/**
 * !IMPORTANT
 * As this is used on multiple pages at the top level we do not pointless renders
 * StickyBarContext updates on every scroll move so we need to memoize this and check that
 * additional renders are prevented on scroll. So we import needed variables then pass to memoised component
 * We use a useEffect hook to check any changes are not causing rerenders
 */
export const SecondaryNavigationEntry = () => {
  const navigation = useSelector((state: IRepoState) => state.LAYOUT.screen.layout.navigation);
  const hasSecondaryNavBar = Boolean(navigation?.links?.length);
  const { dynamicBarPos, stickyItemsState } = useContext(StickyPosition);
  const { searchBarDomRef, secondaryNavBarDomRef } = useContext(DomRefContext);

  // useEffect(() => {
  //   console.log('navRender');
  // }, [secondaryNavBarDomRef, searchBarDomRef]);

  if (hasSecondaryNavBar) {
    return (
      <SecondaryNavigationMemoized
        navigation={navigation}
        stickyItemsState={stickyItemsState}
        searchBarDomRef={searchBarDomRef}
        secondaryNavBarDomRef={secondaryNavBarDomRef}
        dynamicBarPos={dynamicBarPos}
      />
    );
  }
  return null;
};
